var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { modelPool } from "../ModelPool";
import { shouldBroadcast } from "../types";
import { waitForModelWithPredicateAsync, waitForModelWithPredicate } from "../modelPoolUtils";
import { ModelPath } from "./ModelPath";
// This file should be removed once ModelId is fully removed
const ROOT_ID = "root";
const PLAYERS_ID = "players";
const REPLAY_ID = "replay";
const UTILITY_ID = "_utility";
/// Model to path ///
/**
 * Constructs an absolute path from the root model up to this model. If a parent is provided then the ModelPath computes
 * the absolute path of the parent and then just adds the name of model i.e. `PATH/TO/PARENT/model.getName()`
 */
export const modelToAbsolutePath = (model, parent) => {
    const name = model.isRoom() ? `[${model.getName()}]` : model.getName();
    return computeAbsolutePath(parent !== null && parent !== void 0 ? parent : model.getParent(), name);
};
/**
 * Constructs an absolute path from the absolute path of the parent and then just adds the name i.e. `PATH/TO/PARENT/modelName`.
 * Additionally, `isRoom` is needed to specify if the ModelPath points to a model or a room.
 */
export const modelNameToAbsolutePath = (modelName, isRoom, parent) => {
    const name = isRoom ? `[${modelName}]` : modelName;
    return computeAbsolutePath(parent, name);
};
/// Path to model ///
/**
 * Creates a new model, from the provided absolute path. The broadcast properties can be specified to control how and which
 * messages will be created. If a `uid` is provided, then it will be used as the uid of the newly created model.
 */
export const createModelFromPath = (absolutePath, uid, props) => {
    const modelPath = new ModelPath(absolutePath);
    if (modelPath.isRoot())
        throw new Error(`[model] Root already exist, cannot create it (${absolutePath})`);
    const parent = modelPath.getParent();
    if (parent === undefined)
        throw new Error(`[model] Path ${absolutePath} has no parent`);
    const name = modelPath.basename();
    const parentPath = parent.toString();
    let parentModel = getModelFromAbsolutePath(parentPath);
    if (parentModel === undefined) {
        parentModel = createModelFromPath(parentPath, undefined, props);
    }
    return modelPool.createModelFull(name, Object.assign({ parent: parentModel, uid, isRoom: modelPath.isRoom() }, props));
};
/**
 * Searches the ModelPool for a model that matches the specified absolute path. If no model could be found, then undefined
 * is returned instead.
 */
export const getModelFromAbsolutePath = (absolutePath) => {
    const path = new ModelPath(absolutePath);
    const names = path.getHierarchyNames();
    if (names.length === 0)
        return undefined;
    if (names[0] !== ROOT_ID)
        throw new Error(`[model] Path ${path.toString()} doesn't start with 'root'`);
    let curModel = modelPool.getRoot();
    for (let i = 1; i < names.length; i++) {
        const name = names[i];
        const parentName = curModel.getName();
        curModel = curModel.getChild(name);
        if (curModel === undefined) {
            console.debug(`[model] Parent ${parentName} has no child named ${name}, with input path: ${path.toString()}`);
            return undefined;
        }
    }
    return curModel;
};
/**
 * Searches the ModelPool for a model and if it didn't find the model, it will instead create a new model. The broadcast
 * properties can be specified to control how and which messages will be created. If a `uid` is provided, then it will
 * be used as the uid of the newly created model.
 */
export const getOrCreateModelFromAbsolutePath = (absolutePath, props) => {
    let model = getModelFromAbsolutePath(absolutePath);
    if (model === undefined) {
        model = createModelFromPath(absolutePath, props === null || props === void 0 ? void 0 : props.uid, props);
        if (shouldBroadcast(props))
            model.flushMessages();
    }
    return model;
};
/**
 * Searches the ModelPool with the relative path to its ancestor `model`. If no model fits this relative path, then
 * undefined is returned instead.
 */
export const getModelRelativeTo = (model, relativePath) => {
    const names = new ModelPath(relativePath).getHierarchyNames();
    let currentModel = model;
    for (let i = 0; i < names.length; i++) {
        const name = names[i];
        const parentName = currentModel.getName();
        currentModel = currentModel.getChild(name);
        if (currentModel === undefined) {
            console.error(`[model] Parent ${parentName} has no child named ${name}, with model ${model.getName()} and relative path: ${relativePath.toString()}`);
            return undefined;
        }
    }
    return currentModel;
};
/// Session function ///
/**
 * Returns true if the model was created by the session runtime.
 * ToDo: Needs more clarification.
 */
export const modelIsSessionModel = (model) => {
    return computeIsSessionModel(model.getName(), model.getParent(), model.isRoot());
};
/// Wait for model compatibility functions ///
/**
 * Waits until a model with the specified absolute path was created and calls the provided callback with that model.
 * If the model already existed, then the callback is called immediately. When a timeout is provided, an error is thrown
 * if still no model with the specified absolute path was created after the timeout expired.
 */
export const waitForModelWithPath = (absolutePath, callback, timeout) => {
    const model = getModelFromAbsolutePath(absolutePath);
    if (model !== undefined) {
        callback(model);
        return;
    }
    waitForModelWithPredicate(absolutePath, (model) => modelToAbsolutePath(model) === absolutePath, callback, timeout);
};
/**
 * Waits asynchronously until a model with the specified absolute path was created and returns that model.
 * If the model already existed, then the model is returned immediately. When a timeout is provided, an error is thrown
 * if still no model with the specified absolute path was created after the timeout expired.
 */
export const waitForModelWithPathAsync = (absolutePath, timeout) => __awaiter(void 0, void 0, void 0, function* () {
    const model = getModelFromAbsolutePath(absolutePath);
    if (model !== undefined)
        return model;
    return yield waitForModelWithPredicateAsync(absolutePath, (model) => modelToAbsolutePath(model) === absolutePath, timeout);
});
/// Private helper functions ///
const computeAbsolutePath = (parent, name) => {
    if (name !== ROOT_ID) {
        let path = (parent !== undefined ? "/" : "") + `${name}`;
        let curModel = parent;
        while (curModel !== undefined) {
            const name = (curModel.isRoom() && !curModel.isRoot()) ? `[${curModel.getName()}]` : curModel.getName();
            curModel = (curModel.isRoot()) ? undefined : curModel.getParent();
            const divider = (curModel === undefined) ? "" : "/";
            path = divider + name + path;
        }
        return path;
    }
    return name;
};
const computeIsSessionModel = (name, parent, isRoot) => {
    let isSessionModel = false;
    if (isRoot || parent === undefined) {
        isSessionModel = false;
    }
    else if (parent.isRoot()) {
        const id = name;
        if (id !== PLAYERS_ID && id !== REPLAY_ID && id !== UTILITY_ID) {
            isSessionModel = true;
        }
    }
    else {
        isSessionModel = computeIsSessionModel(parent.getName(), parent.getParentOrThrow(), parent.isRoot());
    }
    return isSessionModel;
};
